import { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import cx from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContextualRouting } from 'next-use-contextual-routing';
import { useMount } from 'react-use';
import useSWR from 'swr';
import { Box, Button, Stack, useTheme } from '@mui/material';
import { createParamString, trackEvent, type Units, getUser } from '@surfline/web-common';

import { fetchFavoriteSubregions, fetchForecasterContent } from 'actions/subregion';
import { BRAZE_CONTENT_CARDS } from 'common/constants';
import { usePageCall } from 'common/hooks/usePageCall';
import {
  SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS,
  SL_WEB_SPOT_RECOMMENDATIONS,
} from 'common/treatments';
import type { SpotRecommendation } from 'common/api/spotRecommendations';
import AnonHero from 'components/AnonHero';
import { PLAYED_WEBCAM } from 'components/CamPlayerV2/useTrackCamPlayback';
import ContentCard from 'components/ContentCard';
import EditorialCarousel from 'components/EditorialCarousel';
import EventsAndAlerts from 'components/EventsAndAlerts';
import FavoritesCarouselSnap from 'components/FavoritesCarouselSnap';
import FavoriteRegionFilters from 'components/FavoriteRegionFilters';
import GoogleDFP from 'components/GoogleDFP';
import Greeting from 'components/Greeting';
import HomePageMeta from 'components/HomePageMeta';
import LimitedTimeOfferBanner from 'components/LimitedTimeOfferBanner/LimitedTimeOfferBanner';
import ModalContextual from 'components/ModalContextual';
import SwellEventCardContainer from 'components/SwellEventCardContainer/SwellEventCardContainer';
import SubregionForecastContentGrouped from 'components/SubregionForecastContentGrouped';
import SubregionForecastContentHighlights from 'components/SubregionForecastContentHighlights';
import SubregionForecastContentNow from 'components/SubregionForecastContentNow';
import Favorites from 'containers/Favorites';
import FavoritesForecastOutlook from 'containers/FavoritesForecastOutlook/FavoritesForecastOutlook';
import SubregionForecast from 'containers/SubregionForecast';
import { ForecastOutlookContextProvider } from 'contexts/ForecastOutlookContext';
import { PageContextProvider } from 'contexts/PageContext';
import { UserTypeContext } from 'contexts/UserTypeContext';
import { useMaxWidthTablet, useMinWidthTablet } from 'hooks/useMediaQueries';
import useFavorites, { type Region } from 'hooks/useFavorites';
import useSeenTracker from 'hooks/useSeenTracker';
import useSpotRecommendations from 'hooks/useSpotRecommendations';
import { useIsInternalUser } from 'selectors/user';
import { getForecastContent, getForecastContentLoading } from 'selectors/subregion';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import type { Units as LocalUnits } from 'types/units';
import type { ForecastContent } from 'types/subregion';
import type { SwellEvent } from 'types/swellEvents';
import loadAdConfig from 'utils/adConfig';
import computeLimitedTimePremiumPlusEligibility, {
  shouldShowLimitedTimeOfferBanner,
} from 'utils/computeLimitedTimePremiumPlusEligibility';
import { getPopularSpots } from 'utils/favoritesFetch';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import swellEventsFetch from 'utils/swellEventsFetch';
import { useTreatments, parseTreatmentWithConfig } from 'utils/treatments';

import styles from './Homepage.module.scss';

interface HomepageProps {
  geotarget: string;
  colorSchemeIndex?: number;
  userCountryCode: string;
  units: Units;
}

const MODAL_OPEN_PATHS = ['/favorites'];

const onClickSwellEventCard = (swellEvent: SwellEvent) => {
  trackEvent('Clicked Swell Alert Card', {
    title: swellEvent.name,
    contentType: 'Swell Alert',
    basins: swellEvent.basins.join(','),
    locationCategory: 'Homepage - Top',
    destinationUrl: swellEvent.permalink,
  });
};

const Homepage: React.FC<HomepageProps> = ({
  geotarget,
  userCountryCode,
  colorSchemeIndex,
  units,
}) => {
  const router = useRouter();
  const { makeContextualHref, returnHref } = useContextualRouting();
  const { userType, entitlements, showAds } = useContext(UserTypeContext);
  const isMaxWidthTablet = useMaxWidthTablet();
  const { hasCoreForecastPermissions } = useUserPermissionStatus();
  const treatments = useTreatments();
  const user = useAppSelector(getUser);
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isInternal = useIsInternalUser();
  const { favorites, regions } = useFavorites({
    allRegions: false,
    includeInternalCams: true,
    isInternalUser: isInternal,
  });
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>(regions?.[0]);
  const isMinWidthTablet = useMinWidthTablet();

  const forecastContent: ForecastContent = useAppSelector(getForecastContent);
  const forecastContentLoading: boolean = useAppSelector(getForecastContentLoading);

  const { asPath, query } = router;
  const { subregionForecast } = query;
  const userLocation = user?.location;
  const userTimeZone = userLocation?.time_zone || 'America/Los_Angeles';
  const viewAllLabel = isMaxWidthTablet ? 'View all' : 'View all Favorites';
  const isSubregionForecastActive =
    !userType?.isAnonymous && selectedRegion?.forecastStatus === 'active';
  const showDaysToWatchCTA =
    !hasCoreForecastPermissions && isSubregionForecastActive && !!forecastContent;

  // The only place this is used is for forecasterContent update to allow this with native.
  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'forecast-content',
    isEntitled: hasCoreForecastPermissions,
  });

  const { data: swellEvents } = useSWR(
    `/feed/events?${createParamString({ geotarget })}`,
    swellEventsFetch,
  );

  const { data: popularSpots, error: popularSpotsError } = useSWR(
    userType?.isAnonymous ? `/kbyg/spots/popular?` : undefined,
    () => getPopularSpots(userCountryCode),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 0,
    },
  );

  const unitsWithWaveHeight = useMemo<LocalUnits>(
    () => ({ ...units, waveHeight: units.surfHeight }),
    [units],
  );

  const filteredSubregionFavorites = useMemo(
    () => favorites.filter((fav) => fav.subregionId === selectedRegion?.id),
    [favorites, selectedRegion],
  );

  const isSpotRecommendationsEnabled = treatments?.[SL_WEB_SPOT_RECOMMENDATIONS] === 'on';

  const isSeenTrackerEnabled = isSpotRecommendationsEnabled && !userType?.isAnonymous;

  const recommendationsGeoLocation = userLocation
    ? { lat: userLocation.latitude, lon: userLocation.longitude }
    : { lat: 33.7701, lon: -118.1937 };

  const {
    recommendations: fetchedRecommendations,
    fetchNextPage: fetchNextRecommendationsPage,
    isLoading: recommendationsLoading,
  } = useSpotRecommendations({
    lat: recommendationsGeoLocation.lat,
    lon: recommendationsGeoLocation.lon,
    subregionId: selectedRegion?.id,
    enabled: isSpotRecommendationsEnabled,
    units,
  });

  const seenTracker = useSeenTracker({ enabled: isSeenTrackerEnabled });

  const handleRecommendationFavorited = useCallback(
    (spotId: string) => {
      seenTracker.cancel(spotId);
    },
    [seenTracker],
  );

  // One-time analytics event when a recommendation card scrolls into view
  const handleRecommendationImpression = useCallback(
    (recommendation: SpotRecommendation, index: number) => {
      trackEvent('Recommendation Card Viewed', {
        carouselName: 'Favorites Carousel',
        path: asPath,
        positionNumber: index,
        rank: recommendation.rank,
        source: 'recommendation',
        spotId: recommendation._id,
        spotName: recommendation.name,
        title: 'Home',
      });
    },
    [asPath],
  );

  const recommendationProps = isSpotRecommendationsEnabled
    ? {
        recommendations: fetchedRecommendations,
        isLoadingRecommendations: recommendationsLoading,
        onReachingEnd: fetchNextRecommendationsPage,
        onRecommendationFavorited: handleRecommendationFavorited,
        onRecommendationImpression: handleRecommendationImpression,
      }
    : {};

  const carouselRecommendationProps = isSpotRecommendationsEnabled
    ? {
        ...recommendationProps,
        isLoadingRecommendations: recommendationsLoading,
        showSectionTitles: true,
        onRecommendationVisibleOnActivePage: seenTracker.register,
        onRecommendationHiddenFromActivePage: seenTracker.unregister,
      }
    : {};

  const forecastOutlookFavorites = useMemo(() => {
    if (userType?.isAnonymous) return popularSpots?.data.favorites ?? [];
    return filteredSubregionFavorites;
  }, [filteredSubregionFavorites, popularSpots, userType]);

  const limitedTimePremiumPlusConfig = useMemo(
    () =>
      treatments?.[SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS]
        ? parseTreatmentWithConfig(treatments[SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS])
        : null,
    [treatments],
  );

  const isLimitedTimePremiumPlusEligible = useMemo(() => {
    if (limitedTimePremiumPlusConfig && treatments?.[SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS]) {
      return computeLimitedTimePremiumPlusEligibility(limitedTimePremiumPlusConfig, userTimeZone);
    }
    return false;
  }, [limitedTimePremiumPlusConfig, treatments, userTimeZone]);

  const showBanner = shouldShowLimitedTimeOfferBanner(
    isLimitedTimePremiumPlusEligible,
    treatments,
    SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS,
  );

  const adTarget = useMemo(() => [['qaFlag', !!query?.qaFlag]], [query?.qaFlag]);

  const contextualHref = useMemo(() => makeContextualHref({ type: 'live' }), [makeContextualHref]);

  const segmentProperties = useMemo(
    () => ({
      userType,
      category: 'home',
      channel: 'home',
      subCategory: 'personalized',
    }),
    [userType],
  );

  usePageCall('Home', segmentProperties, !!router);

  const handleRegionSelected = useCallback(
    (event: React.MouseEvent<HTMLElement>, newRegions: Array<string> | string) => {
      trackEvent('Clicked Filter', {
        filterName: 'subregion',
        pageName: 'Home',
      });
      const regionId = Array.isArray(newRegions) ? newRegions[0] : newRegions?.toString();
      const region = regions?.find((r) => r.id === regionId);
      if (region) setSelectedRegion(region);
    },
    [regions],
  );

  const onClickHandlerLive = useCallback(() => {
    trackEvent('Link Clicked', {
      linkName: 'view all favorites',
      linkUrl: '/favorites?type=live',
      location: 'home carousel favorites',
      pageName: 'Home',
    });
  }, []);

  useEffect(() => {
    const modalShouldBeOpen = MODAL_OPEN_PATHS.includes(asPath.split('?')[0]);
    setModalOpen(modalShouldBeOpen);
  }, [asPath]);

  useEffect(() => {
    if (isSubregionForecastActive) {
      dispatch(
        fetchForecasterContent({
          cookies: undefined,
          days: forecastDaysAllowed,
          subregionId: selectedRegion?.id,
        }),
      );
    }
  }, [
    forecastDaysAllowed,
    dispatch,
    hasCoreForecastPermissions,
    isSubregionForecastActive,
    selectedRegion,
  ]);

  useMount(() => {
    trackEvent(PLAYED_WEBCAM, {
      category: 'Homepage Spot Preview Card',
      premiumCam: false,
      spotName: 'dummy',
      camName: 'dummy',
      camId: 'dummy',
      favorite: false,
      spotId: 'dummy',
      videoType: 'live',
    });

    dispatch(fetchFavoriteSubregions(undefined));
  });

  return (
    <PageContextProvider pageName="Home">
      <HomePageMeta />
      {showBanner && <LimitedTimeOfferBanner splitConfig={limitedTimePremiumPlusConfig} />}
      {swellEvents?.length && !userType?.isAnonymous ? (
        <section className="sl-section-container" data-testid="homepage-swell-events-top">
          <SwellEventCardContainer events={swellEvents || []} onClickCard={onClickSwellEventCard} />
        </section>
      ) : null}
      <section className="sl-section-container">
        <ContentCard
          card={{
            name: BRAZE_CONTENT_CARDS.HOMEPAGE_HEADER,
            extras: {
              backgroundColor: '',
              body: '',
              closeButtonColor: '',
              fontColor: '',
              linkText: '',
              linkUrl: '',
            },
          }}
          className={styles.contentCardHeader}
          testId="banner-braze-card-homepage-header"
        />
      </section>
      <Box className={styles.hideOverflow}>
        <ForecastOutlookContextProvider
          hasCoreForecastPermissions={hasCoreForecastPermissions}
          surfHeightUnit={units?.surfHeight}
        >
          <ModalContextual open={modalOpen} returnHref={returnHref}>
            <PageContextProvider pageName="Favorites Modal">
              <Favorites surfHeightUnit={units?.surfHeight} />
            </PageContextProvider>
          </ModalContextual>
          {userType.isAnonymous ? (
            <AnonHero
              geoTarget={geotarget}
              colorSchemeIndex={colorSchemeIndex}
              spotsResponse={popularSpots}
              units={unitsWithWaveHeight}
              error={popularSpotsError}
              {...recommendationProps}
            />
          ) : (
            <>
              <Box className="sl-section-container" component="section" my={3}>
                <Greeting firstName={user?.details?.firstName} />
                {!!favorites.length && (
                  <Stack direction="row" spacing={2}>
                    <div className={styles.regionFiltersWrapper}>
                      <FavoriteRegionFilters
                        exclusive
                        favoriteRegions={regions}
                        handleRegionSelected={handleRegionSelected}
                        selectedRegions={selectedRegion?.id}
                      />
                    </div>
                    <Box className={styles.regionalFiltersActions}>
                      <Link href={contextualHref} as="/favorites?type=live" shallow>
                        <Button
                          className={styles.viewAllButton}
                          data-testid="homepage-view-all-favorites"
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          onClick={onClickHandlerLive}
                          size="small"
                          variant="secondary"
                        >
                          {viewAllLabel}
                        </Button>
                      </Link>
                    </Box>
                  </Stack>
                )}
                {isSubregionForecastActive && !isMinWidthTablet && (
                  <Box my={2}>
                    <SubregionForecastContentNow
                      elevation={0}
                      forecastContent={forecastContent}
                      isLoading={forecastContentLoading}
                      isTitleOnly
                      pageName="Home"
                    />
                  </Box>
                )}
              </Box>
              <Box className={styles.favoritesCarouselSnap}>
                <FavoritesCarouselSnap
                  favorites={filteredSubregionFavorites}
                  isActive={!modalOpen}
                  scrollResetKey={selectedRegion?.id}
                  units={unitsWithWaveHeight}
                  {...carouselRecommendationProps}
                />
              </Box>
              {isSubregionForecastActive && (
                <Box className="sl-section-container" mt={4}>
                  {!isMinWidthTablet && !showDaysToWatchCTA && (
                    <SubregionForecastContentHighlights
                      elevation={0}
                      forecastContent={forecastContent}
                      isLoading={forecastContentLoading}
                      isTodayIncluded={false}
                      pageName="Home"
                    />
                  )}
                  {isMinWidthTablet && (
                    <SubregionForecastContentGrouped
                      elevation={0}
                      forecastContent={forecastContent}
                      isLoading={forecastContentLoading}
                      pageName="Home"
                    />
                  )}
                </Box>
              )}
            </>
          )}
          <section
            className="sl-section-container"
            style={
              !favorites.length
                ? undefined
                : {
                    marginTop: '40px',
                  }
            }
          >
            <ContentCard
              card={{
                name: BRAZE_CONTENT_CARDS.HOMEPAGE_OUTLOOK,
                extras: {
                  backgroundColor: '',
                  body: '',
                  closeButtonColor: '',
                  fontColor: '',
                  linkText: '',
                  linkUrl: '',
                },
              }}
              className={styles.contentCardOutlook}
              testId="banner-braze-card-homepage-outlook"
            />
          </section>
          <FavoritesForecastOutlook
            favorites={forecastOutlookFavorites}
            favoritesError={!!popularSpotsError}
            isSubregionForecastActive={isSubregionForecastActive}
            showDaysToWatchCTA={showDaysToWatchCTA}
            showMissingFavourites={!isSpotRecommendationsEnabled}
          />
        </ForecastOutlookContextProvider>
        {showAds && (
          <div className={styles.ad} data-testid="homepage-ad">
            <GoogleDFP
              adConfig={loadAdConfig(
                'homepage_feed_top',
                adTarget,
                entitlements,
                !userType.isAnonymous,
                null,
              )}
            />
          </div>
        )}
        {swellEvents?.length && userType?.isAnonymous ? (
          <EventsAndAlerts>
            <SwellEventCardContainer
              events={swellEvents || []}
              onClickCard={onClickSwellEventCard}
            />
          </EventsAndAlerts>
        ) : null}
        <Box mt="50px">
          <EditorialCarousel geotarget={geotarget} />
        </Box>
        {showAds && (
          <div className={cx(styles.ad, styles.bottomAd)} data-testid="homepage-ad">
            <GoogleDFP
              adConfig={loadAdConfig(
                'homepage_feed_bottom',
                adTarget,
                entitlements,
                !userType.isAnonymous,
                null,
              )}
            />
          </div>
        )}
      </Box>
      <ModalContextual
        backgroundColor={theme.palette.secondary.main}
        open={!!subregionForecast}
        returnHref={returnHref}
        scrollContainerId="forecaster-content-modal"
      >
        <SubregionForecast scrollContainerId="forecaster-content-modal" isSubregionListVisible />
      </ModalContextual>
    </PageContextProvider>
  );
};

export default Homepage;
